import { type FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isNotEmpty, _notNil } from '@/littledash';

import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSexLabel } from '@/model/Animal.model';
import type { AnimalSex, AnimalSpecies } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMetaV16 } from '@/support/hot-v16';
import type { HoTMeta } from '@/support/hot';
import { web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { featuresSelector } from '@/support/Selectors';
import { MAX_CAGE_POPULATION } from '@/constants/cage';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import SubHeader from '@/components/UI/SubHeader';
import { successToast } from '@/helpers';

import { fetchExactCageData, getMetaData } from '../Colony.utils';
import { initAnimalSettingsV16, initialAddAnimalDataV16 } from '../Colony.utils.v16';
import type { AddAnimalRowV16 } from '../Colony.utils.v16';
import AddAnimalsTableV16 from './AddAnimalsTableV16';
import '../Colony.scss';

interface AnimalPostData {
  cage_id?: string;
  name: string;
  dob?: string | null;
  sex?: AnimalSex;
  strain_id?: string;
  metadata?: Array<{ glossary_id: string; value: unknown }>;
}

const featureFlagsSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

const AddAnimals: FC = () => {
  const { id: cageID } = useParams();
  const navigate = useNavigate();
  const { unknownSexEnabled } = useSelector(featureFlagsSelector);

  const tableRef = useRef<HotTableRef>(null);

  const [data, setData] = useState<Array<AddAnimalRowV16>>([]);
  const [settings, setSettings] = useState<SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [cageData, setCageData] = useState<Cage | undefined>();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [animalCount, setAnimalCount] = useState<number>(0);
  const [species, setSpecies] = useState<Array<AnimalSpecies>>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const getHotInstance = () => {
    return tableRef.current?.hotInstance ?? null;
  };

  useEffect(() => {
    const result = initAnimalSettingsV16(
      species,
      true,
      true,
      undefined,
      undefined,
      undefined,
      entityFieldRequiredMap,
      unknownSexEnabled
    );
    setSettings(result.settings);
  }, [species, entityFieldRequiredMap, unknownSexEnabled]);

  useEffect(() => {
    if (_notNil(cageData)) {
      setData(initialAddAnimalDataV16(cageData));
      checkAnimalLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cageData]);

  const { response: speciesResponse } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  useEffect(() => {
    if (speciesResponse?.type === 'success') {
      setSpecies(speciesResponse?.body?.data as unknown as Array<AnimalSpecies>);
    }
  }, [speciesResponse]);

  const getInitialDependencies = async () => {
    setLoading(true);
    try {
      const metadataResponse = await getMetaData(MetadataTypes.subject_meta);
      const cage = await fetchExactCageData(cageID as ID);
      setMetadata((metadataResponse?.body ?? []) as Array<MetadataField>);
      setCageData(cage.data.data);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInitialDependencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const afterAddRow = () => {
    setUpdating(true);
  };

  useEffect(() => {
    checkAnimalLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, updating]);

  const checkAnimalLimit = (removeRow = false, amountRemoved = 0) => {
    const hot = getHotInstance();
    const animalsToAdd = _notNil(hot) ? hot.countRenderedRows() : 1;
    if (_notNil(cageData) && _notNil(cageData.counts)) {
      const animalInCage = cageData.counts.animals ?? 0;
      if (removeRow) {
        setAnimalCount(animalCount - amountRemoved);
      } else {
        setAnimalCount(animalsToAdd + animalInCage);
      }
    } else {
      setAnimalCount(animalCount + animalsToAdd);
    }
    setUpdating(false);
  };

  const { invoke: postAnimals } = useApiHook({
    endpoint: 'POST /api/v1/animals',
    body: { animals: [] },
    options: {
      invokeOnInit: false,
      onError: { slug: 'colony-create-animals', capture: true, toast: false },
    },
  });

  const mapAnimalPostData = (rowData: Array<AddAnimalRowV16>): Array<AnimalPostData> =>
    rowData.map(({ name, sex, dob, species: animalSpecies, strain, meta }) => {
      const returnData: AnimalPostData = {
        cage_id: cageData?.api_id,
        name,
        dob: dob ?? null,
      };
      if (_notNil(sex)) {
        returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }
      if (_notNil(animalSpecies)) {
        const matchingSpecies = species.find((s) => s.name === animalSpecies);
        if (_notNil(matchingSpecies) && _isNotEmpty(matchingSpecies.strains)) {
          const matchingStrain = matchingSpecies.strains?.find((s) => s.name === strain);
          if (_notNil(matchingStrain) && _notNil(matchingStrain.api_id)) {
            returnData.strain_id = matchingStrain.api_id;
          }
        }
      }
      if (_isNotEmpty(meta) && _notNil(settings) && _notNil(settings.columns)) {
        const rawMeta = createMetaV16(meta, settings.columns) as Array<HoTMeta>;
        const glossaryIdToApiId = metadata.reduce<Record<string, string>>((acc, g) => {
          if (_notNil(g.id) && _notNil(g.api_id)) {
            acc[String(g.id)] = g.api_id;
          }
          return acc;
        }, {});
        returnData.metadata = rawMeta
          .filter((m) => _notNil(m.glossary_id))
          .map((m) => ({
            glossary_id: glossaryIdToApiId[String(m.glossary_id)] ?? String(m.glossary_id),
            value: m.value,
          }));
      }
      return returnData;
    });

  const saveAnimals = async () => {
    setLoading(true);
    const animals = mapAnimalPostData(data);
    try {
      await postAnimals({ body: { animals } });
      navigate('/colony');
      successToast(`Successfully added ${animals.length > 1 ? 'animals' : 'animal'}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const addRowConfig = {
    tooltip: animalCount >= MAX_CAGE_POPULATION ? 'Maximum cage population reached.' : '',
    title: 'Animal',
  };

  const defaultNewRowParams = [
    { col: 2, value: cageData?.name ?? '' },
    { col: 4, value: `Animal ${animalCount + 1}` },
  ];

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title="There was an error with your request"
          text="An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support."
          error={apiError}
        />
      )}
      <SubHeader linkToText="Colony" link={webRoute('colony')} />
      <div className="ph4">
        <Header mainHeaderText="Add new animals" />
        <div className="flex-row items-start flex flex-wrap mb3">
          <p className="lh-copy set-width">
            <span className="mr2">Add new animals to {cageData?.name}</span>
          </p>
        </div>
      </div>
      {loading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Updating cage..." />
        </div>
      ) : (
        _notNil(settings) && (
          <div className="ow-spreadsheet-styles">
            <AddAnimalsTableV16
              data={data as unknown as Array<Record<string, unknown>>}
              settings={settings}
              innerRef={tableRef as React.RefObject<HotTableRef>}
              metadata={metadata}
              setMetadata={setMetadata}
              setInvalid={setIsInvalid}
              addRow={addRowConfig}
              afterAddRow={afterAddRow}
              defaultNewRowParams={defaultNewRowParams}
            />
          </div>
        )
      )}
      <div className="pa4">
        <Button
          onClick={saveAnimals}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate('/colony')}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AddAnimals;
